import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCardOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/MenuOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';

import { useAppDispatch } from '@/app/hooks';
import { setCommandPaletteOpen } from '@/app/uiSlice';
import { useAuth } from '@/features/auth/AuthProvider';
import { brand } from '@/theme/brand';
import { Wordmark } from './Wordmark';

export interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  end?: boolean;
  badge?: string;
  /** Hide from RESTAURANT_STAFF — owner/admin only. */
  ownerOnly?: boolean;
}

export interface AppShellProps {
  nav: NavItem[];
  /** Shown under the wordmark, e.g. the restaurant name or "Admin". */
  context?: string;
  children: ReactNode;
}

const DRAWER_WIDTH = 240;

export function AppShell({ nav, context, children }: AppShellProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const isAdmin = user?.role === 'ADMIN';
  const isOwner = user?.role === 'RESTAURANT_OWNER' || isAdmin;
  const visibleNav = nav.filter((item) => !item.ownerOnly || isOwner);

  const openCommandPalette = () => dispatch(setCommandPaletteOpen(true));

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2.5, minHeight: 64 }}>
        <Stack spacing={0.5}>
          <Wordmark size={18} />
          {context && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ maxWidth: 180, lineHeight: 1.2 }}
            >
              {context}
            </Typography>
          )}
        </Stack>
      </Toolbar>
      <Divider />

      <List sx={{ px: 1.25, py: 1.5, flexGrow: 1 }}>
        {visibleNav.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            sx={{
              mb: 0.25,
              py: 0.85,
              px: 1.5,
              borderRadius: '10px',
              color: 'text.primary',
              transition: 'background 120ms, color 120ms',
              '&:hover': {
                bgcolor: 'action.hover',
              },
              '&.active': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'primary.main',
                },
                '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34, color: 'text.secondary' }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 'inherit' } } }}
            />
            {item.badge && (
              <Typography
                variant="caption"
                sx={{
                  color: 'inherit',
                  opacity: 0.7,
                  fontWeight: 500,
                }}
              >
                {item.badge}
              </Typography>
            )}
          </ListItemButton>
        ))}
      </List>

      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.75, alignItems: 'center' }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>
          {user?.name?.[0]?.toUpperCase() ?? '?'}
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {user?.name}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
            {user?.email}
          </Typography>
        </Box>
        <Tooltip title="Account">
          <IconButton
            size="small"
            aria-label="Account menu"
            onClick={(event) => setAccountAnchor(event.currentTarget)}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: `${brand.radius.cards}px`,
              border: 1,
              borderColor: 'divider',
              boxShadow: 'none',
              mt: -0.5,
              minWidth: 180,
            },
          },
        }}
      >
        {!isAdmin && isOwner && (
          <MenuItem
            onClick={() => {
              setAccountAnchor(null);
              void navigate('/app/billing');
            }}
          >
            <ListItemIcon>
              <CreditCardIcon fontSize="small" />
            </ListItemIcon>
            Plan
          </MenuItem>
        )}
        {(isAdmin || isOwner || user?.role === 'RESTAURANT_STAFF') && (
          <MenuItem
            onClick={() => {
              setAccountAnchor(null);
              void navigate(isAdmin ? '/admin/environment' : '/app/settings');
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {isAdmin ? 'Environment' : 'Settings'}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAccountAnchor(null);
            void logout().then(() => navigate('/', { replace: true }));
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            px: { xs: 2, md: 3 },
            py: 1.25,
            minHeight: 56,
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Search and jump anywhere">
            <Box
              component="button"
              type="button"
              onClick={openCommandPalette}
              sx={{
                appearance: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                m: 0,
                px: 1.5,
                py: 0.75,
                borderRadius: `${brand.radius.pills}px`,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'transparent',
                color: 'text.secondary',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: '0.02em',
                transition: 'border-color 120ms ease, color 120ms ease, background-color 120ms ease',
                '&:hover': {
                  borderColor: 'text.primary',
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Search
              </Box>
              <Typography
                component="kbd"
                variant="caption"
                sx={{
                  display: { xs: 'none', md: 'inline' },
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  fontFamily: brand.monoFamily,
                  fontSize: 11,
                  lineHeight: 1.4,
                }}
              >
                Ctrl K
              </Typography>
            </Box>
          </Tooltip>
        </Stack>

        <Box
          component="main"
          sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
